// src/Service/windService.js
import { api, DEFAULT_DEPLOYMENT } from './fetchDeployments';
import store from '@/store';

function createEmptyWindData() {
  return {
    time: [],
    speed: [],
    direction: [],
    gust: [],
    success: true,
    noDataAvailable: true
  };
}

export async function fetchWindData(site, depth = 0) {
  try {
    if (!isValidDeploymentSite(site)) {
      console.warn('Invalid deployment parameters');
      return { 
        ...createEmptyWindData(), 
        success: false, 
        error: 'Invalid parameters' 
      };
    }

    const payload = {
      site: site,
      depth: Number(depth),
      timestamp: new Date().toISOString()
    };

    const response = await api.post('/windData', payload);

    // Handle 204 No Content
    if (response.status === 204) {
      return { 
        ...createEmptyWindData(),
        success: true,
        noDataAvailable: true 
      };
    }

    // Handle 200 Success
    if (response.status === 200 && response.data) {
      const windData = {
        time: ensureArray(response.data.time),
        speed: ensureNumericArray(response.data.speed),
        direction: ensureNumericArray(response.data.direction),
        gust: ensureNumericArray(response.data.gust),
        success: true,
        noDataAvailable: false
      };

      if (!isValidWindData(windData)) {
        return { 
          ...createEmptyWindData(),
          success: true,
          noDataAvailable: true 
        };
      }

      return windData;
    }

    // Handle unexpected status
    return { 
      ...createEmptyWindData(),
      success: true,
      noDataAvailable: true 
    };

  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Wind data fetch error:', error.message);
    }

    return { 
      ...createEmptyWindData(), 
      success: false, 
      error: 'Unable to fetch wind data' 
    };
  }
}

function isValidWindData(data) {
  if (!data || typeof data !== 'object') return false;
  
  const requiredFields = ['time', 'speed', 'direction', 'gust'];
  const hasAllFields = requiredFields.every(field => Array.isArray(data[field]));
  
  if (!hasAllFields) return false;
  
  const lengths = requiredFields.map(field => data[field].length);
  return lengths.every(len => len === lengths[0] && len > 0);
}

function isValidDeploymentSite(site) {
  if (typeof site !== 'string' || !site.trim()) {
    return false;
  }

  if (site === DEFAULT_DEPLOYMENT.site) {
    return true;
  }

  const deployments = store.getters.getDeployments;
  if (!deployments) {
    return site === DEFAULT_DEPLOYMENT.site;
  }

  return deployments.some(deployment => deployment.site === site);
}

function ensureArray(data) {
  if (!data) return [];
  return Array.isArray(data) ? data : [];
}

function ensureNumericArray(data) {
  if (!data) return [];
  return Array.isArray(data) ? data.map(num => {
    const parsed = Number(num);
    return isNaN(parsed) ? 0 : parsed;
  }) : [];
}

export async function getCurrentDeploymentWindData() {
  try {
    const currentDeployment = store.getters.getCurrentDeployment;
    if (!currentDeployment) {
      return { 
        ...createEmptyWindData(), 
        success: false, 
        error: 'No deployment selected' 
      };
    }
    
    return await fetchWindData(currentDeployment.site);
  } catch (error) {
    return { 
      ...createEmptyWindData(), 
      success: false, 
      error: 'Unable to fetch current deployment data' 
    };
  }
}

export {
  DEFAULT_DEPLOYMENT
};